// deps
import { useReducer, Dispatch, useMemo, useCallback, useContext } from "react";

// internal deps
import { WalletPaymentMethodData } from "@swiggy-private/payment-ui";
import { VALID_WALLET_LINK_STATUSES, WalletApiFetchState } from "@swiggy-private/payment-core";
import { Result, Ok, Err } from "@swiggy-private/js-fp";
import { useMountedRef } from "@swiggy-private/core-ui";

// reducers
import {
    getWalletBalanceReducer,
    GetWalletBalanceActionTypes,
    GetWalletBalanceAction,
    WalletBalanceState,
    defaultWalletBalanceStateValue,
} from "@payment-client/reducers/Wallet";

// interfaces
import { GetWalletBalanceResponse } from "@payment-client/interfaces/WalletApi";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// helpers
import { isRedirectFlow, getAllWalletMethods } from "@payment-client/helpers/WalletHelper";
import { WalletBalanceContextValue } from "@payment-client/contexts/WalletBalanceContext";

// contexts
import { ApiContext, recordNREvent, NEWRELIC_TABLES, WP_WALLET_BALANCE_STATE } from "@swiggy-private/checkout-core";
import { transformBalanceInfo } from "@payment-client/helpers/PaymentHelper";
import { getWalletBalancePublic } from "@payment-client/apis/publicWallet";

interface FetchWalletBalanceResponse {
    name: string;
    response: Result<GetWalletBalanceResponse, Error | string | unknown>;
    isFromBalanceInfo: boolean;
}

const defaultBalanceResponse: GetWalletBalanceResponse = {
    linkStatus: VALID_WALLET_LINK_STATUSES.NEVER_LINKED,
    balance: 0,
    isOldWalletLinkFlow: false,
    mobileNumber: "",
};

async function fetchWalletBalance(
    apiHost: string,
    paymentMethod: WalletPaymentMethodData,
): Promise<FetchWalletBalanceResponse> {
    // When GPO serves the balance inline, the API is skipped — isFromBalanceInfo lets us
    // distinguish the source in the funnel.
    const isFromBalanceInfo = !!paymentMethod.balanceInfo;

    // (2.1) Signal that a balance fetch was attempted at all (inline or via API).
    recordNREvent({
        eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
        state: WP_WALLET_BALANCE_STATE.FETCH_START,
        walletCode: paymentMethod.name,
        isFromBalanceInfo: String(isFromBalanceInfo),
    });

    try {
        if (paymentMethod.balanceInfo) {
            const balanceInfo = transformBalanceInfo(paymentMethod.balanceInfo);
            return {
                name: paymentMethod.name,
                response: Ok(balanceInfo),
                isFromBalanceInfo,
            };
        }

        const response = await getWalletBalancePublic(apiHost, paymentMethod.name);
        return {
            name: paymentMethod.name,
            response: Ok(response ?? defaultBalanceResponse),
            isFromBalanceInfo,
        };
    } catch (err) {
        return { name: paymentMethod.name, response: Err(err), isFromBalanceInfo };
    }
}

function processFetchWalletBalanceResponse(
    { name, response, isFromBalanceInfo }: FetchWalletBalanceResponse,
    dispatch: Dispatch<GetWalletBalanceAction>,
    payableAmount?: string,
): void {
    response.match({
        ok: (result) => {
            const balance = Number(result.balance) || 0;
            const payload = { ...result, balance };

            const hasPayableAmount = payableAmount != null && payableAmount !== "";
            const isWalletBalanceSufficient = hasPayableAmount ? balance >= Number(payableAmount) : "--";

            // (2.2) Per-wallet visibility: which wallets are shown with sufficient vs insufficient balance.
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
                state: WP_WALLET_BALANCE_STATE.FETCH_SUCCESS,
                walletCode: name,
                balance: String(balance > 0),
                linkStatus: result.linkStatus ?? "",
                isWalletBalanceSufficient: String(isWalletBalanceSufficient),
                payableAmount: String(Number(payableAmount) > 0),
                isFromBalanceInfo: String(isFromBalanceInfo),
            });

            dispatch({ name, type: GetWalletBalanceActionTypes.SUCCESS, payload });
        },
        err: (error) => {
            // (2.3) Per-provider balance fetch error rate — previously dispatched with no NR event.
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
                state: WP_WALLET_BALANCE_STATE.FETCH_ERROR,
                walletCode: name,
                errorMessage: (error as Error)?.message ?? String(error ?? "unknown"),
            });
            dispatch({ name, type: GetWalletBalanceActionTypes.ERROR });
        },
    });
}

function defaultReducerStateBuilder(paymentMethods: WalletPaymentMethodData[]): WalletBalanceState {
    return paymentMethods.reduce<WalletBalanceState>((acc, cur) => {
        acc[cur.name] = {
            ...defaultWalletBalanceStateValue,
            fetchState: isRedirectFlow(cur) ? WalletApiFetchState.SUCCESS : WalletApiFetchState.DEFAULT,
            payableAmount: cur?.payableAmount,
        };
        return acc;
    }, {});
}

interface WalletBalanceProps {
    paymentResponse: GetPaymentMethodsResponse;
}

export function useWalletBalance({ paymentResponse }: WalletBalanceProps): WalletBalanceContextValue {
    const isMountedRef = useMountedRef();
    const { host } = useContext(ApiContext);

    // Memoize paymentMethods with a more stable dependency
    const paymentMethods = useMemo(
        () => getAllWalletMethods(paymentResponse.paymentGroup),
        [paymentResponse.paymentGroup],
    );
    const [reducerState, dispatch] = useReducer(getWalletBalanceReducer, paymentMethods, defaultReducerStateBuilder);
    const apiHost = host.unwrap();

    const refetchBalance = useCallback(
        async (name: string): Promise<void> => {
            const method = paymentMethods.find((method) => method.name === name);
            if (!method) {
                return;
            }

            dispatch({ type: GetWalletBalanceActionTypes.REQUEST, name });
            const response = await fetchWalletBalance(apiHost, method);
            if (isMountedRef.current) {
                processFetchWalletBalanceResponse(response, dispatch, method.payableAmount);
            }
        },
        [apiHost, isMountedRef, paymentMethods],
    );

    // Memoize the return value to prevent unnecessary re-renders of consumers
    const contextValue = useMemo(
        () => ({
            setState: dispatch,
            state: reducerState,
            refetch: refetchBalance,
        }),
        [reducerState, refetchBalance],
    );

    return contextValue;
}
